import { useState, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../../api'
import { useMe } from '../../hooks/useMe'
import { C, F } from './theme'
import { Card, SectionHead, Btn, Icon, LockedCard } from './prims'
import { hasProposedSlot } from './derive'
import type { ForecastPayload } from '../../types'

// Business plan panel (Overview). Generate a Claude-composed plan (gated until
// the forecast is filled in + research done), download it, upload an edited
// final version (PDF/DOCX — becomes the baseline), and download any version.
// Re-generating builds on the latest uploaded version and adds a new one.

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => { const s = String(r.result); const c = s.indexOf(','); resolve(c >= 0 ? s.slice(c + 1) : s) }
    r.onerror = () => reject(r.error)
    r.readAsDataURL(file)
  })
}
const fmtDate = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }

export function BusinessPlanCard({ eventId, fc }: { eventId: number; fc: ForecastPayload }) {
  const { data: me } = useMe()
  const qc = useQueryClient()
  const docsQ = useQuery({ queryKey: ['plan-docs', eventId], queryFn: () => ApiClient.forecastPlanDocs(eventId), enabled: !!me?.hasFinancial })
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  if (!me?.hasFinancial) return <LockedCard label="The business plan needs financial access" />

  const docs = docsQ.data?.docs ?? []
  const missing: string[] = []
  if (!hasProposedSlot(fc)) missing.push('a proposed slot')
  if (!((fc.attendanceFc ?? 0) > 0 && (fc.ticketYieldPence ?? 0) > 0)) missing.push('baseline attendance & yield')
  if (!(fc.costLines.length > 0 || fc.revenueStreams.length > 0)) missing.push('a cost or revenue line')
  if (!((fc.insights?.length ?? 0) > 0 || !!fc.researchSummary)) missing.push('market research')
  const ready = missing.length === 0

  const generate = async () => {
    if (!ready || busy) return
    setErr(null); setBusy('generate')
    try {
      const res = await fetch(`/api/events/${eventId}/business-plan.pdf`, { method: 'POST' })
      if (!res.ok) throw new Error(String(res.status))
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = `Business Plan — ${fc.eventId}.pdf`
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
      void qc.invalidateQueries({ queryKey: ['plan-docs', eventId] })
    } catch { setErr('Could not generate — Claude composes it, so give it a few seconds and retry.') } finally { setBusy(null) }
  }
  const onFile = async (file: File) => {
    setErr(null)
    if (!/\.(pdf|docx)$/i.test(file.name)) { setErr('PDF or Word (.docx) only.'); return }
    if (file.size > 15 * 1024 * 1024) { setErr('That file is over the 15 MB limit.'); return }
    setBusy('upload')
    try { await ApiClient.uploadPlanDoc(eventId, file.name, await fileToBase64(file)); void qc.invalidateQueries({ queryKey: ['plan-docs', eventId] }) }
    catch { setErr('Upload failed.') } finally { setBusy(null) }
  }

  return (
    <Card style={{ marginBottom: 16 }}>
      <input ref={fileRef} type="file" accept=".pdf,.docx,application/pdf" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = '' }} />
      <SectionHead icon="file" label="Business plan" right={
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn variant="secondary" small icon="download" disabled={!!busy} onClick={() => fileRef.current?.click()}>{busy === 'upload' ? 'Uploading…' : 'Upload final version'}</Btn>
          <Btn variant="primary" small icon="sparkle" disabled={!ready || !!busy} onClick={generate}>{busy === 'generate' ? 'Generating…' : 'Generate with Claude'}</Btn>
        </div>
      } />

      {!ready && (
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, background: C.brassBg, border: `1px solid ${C.brass}22`, marginBottom: 10 }}>
          <Icon name="info" size={15} color={C.brass} />
          <span style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft }}>Add {missing.join(', ')} to enable Claude generation. You can still upload a plan in the meantime.</span>
        </div>
      )}
      {busy === 'generate' && <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, marginBottom: 8 }}>Claude is composing the plan and rendering the PDF — this can take up to a minute.</div>}
      {err && <div style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.oxblood, marginBottom: 8 }}>{err}</div>}

      {docs.length === 0 ? (
        <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkFaint }}>No versions yet. Generate one with Claude, or upload your own.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {docs.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 9, border: `1px solid ${C.lineSoft}`, background: C.card }}>
              <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 800, color: C.ink, minWidth: 28 }}>v{d.version}</span>
              <span style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: d.source === 'uploaded' ? C.brassDk : C.green, background: d.source === 'uploaded' ? C.brassBg : C.greenBg, borderRadius: 5, padding: '2px 7px' }}>{d.source === 'uploaded' ? 'Uploaded' : 'Claude'}</span>
              <span style={{ flex: 1, minWidth: 0, fontFamily: F.body, fontSize: 12.5, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.filename}</span>
              <span style={{ fontFamily: F.mono, fontSize: 10.5, color: C.inkFaint, whiteSpace: 'nowrap' }}>{fmtDate(d.createdAt)}{d.createdByName ? ` · ${d.createdByName}` : ''}</span>
              <a href={`/api/forecast/plan-docs/${d.id}/download`} target="_blank" rel="noopener noreferrer" title="Download" style={{ display: 'flex', color: C.green }}><Icon name="download" size={16} /></a>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontFamily: F.body, fontSize: 11, color: C.inkFaint, marginTop: 10 }}>
        Uploading a version becomes the baseline. Re-generating with Claude builds on your latest upload and adds a new version — your uploaded version is never overwritten.
      </div>
    </Card>
  )
}
